import { useEffect, useMemo, useRef, useState } from "react";
import type { AnalysisProgress, Region } from "../types";

const ANALYSIS_STEPS = [
  "ldp_explore_image_analysis_step_color",
  "ldp_explore_image_analysis_step_texture",
  "ldp_explore_image_analysis_step_catalog",
];

function createInitialProgress(regionIds: string[]): AnalysisProgress[] {
  return regionIds.map((id) => ({
    regionId: id,
    percentage: 0,
    currentStep: 0,
    steps: ANALYSIS_STEPS.map((labelKey) => ({
      labelKey,
      complete: false,
    })),
  }));
}

const HOLD_PERCENTAGE = 90;

export function useAnalysisProgress(
  regions: Region[],
  active: boolean,
  apiDone: boolean,
) {
  const regionIdKey = regions.map((r) => r.id).join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const regionIds = useMemo(() => regions.map((r) => r.id), [regionIdKey]);
  const [progress, setProgress] = useState<AnalysisProgress[]>(() =>
    createInitialProgress(regionIds),
  );
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const apiDoneRef = useRef(apiDone);

  useEffect(() => {
    apiDoneRef.current = apiDone;
    if (!apiDone) return;
    setProgress((prev) =>
      prev.map((p) => ({
        ...p,
        percentage: 100,
        currentStep: ANALYSIS_STEPS.length,
        steps: p.steps.map((s) => ({ ...s, complete: true })),
      })),
    );
  }, [apiDone]);

  useEffect(() => {
    if (!active) return;

    setProgress(createInitialProgress(regionIds));
    setIsComplete(false);

    const totalSteps = ANALYSIS_STEPS.length;
    const tickInterval = 100;

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const done = apiDoneRef.current;
        const next = prev.map((p) => {
          if (p.currentStep >= totalSteps) return p;

          const cap = done ? 100 : HOLD_PERCENTAGE;
          // Ease toward the cap: cover a fixed fraction of the remaining
          // distance each tick (fast early, gentle near the cap) with a small
          // floor so the bar never looks frozen while the API calls run. When
          // the API is done we close the last gap quickly toward 100.
          const remaining = cap - p.percentage;
          const easeFactor = done ? 0.25 : 0.04;
          const minStep = done ? 1.5 : 0.25;
          const increment = Math.max(minStep, remaining * easeFactor);
          const newPercentage = Math.min(cap, p.percentage + increment);
          const stepsCompleted = Math.min(
            done ? totalSteps : totalSteps - 1,
            Math.floor((newPercentage / 100) * totalSteps),
          );
          const newSteps = p.steps.map((s, i) => ({
            ...s,
            complete: i < stepsCompleted,
          }));

          return {
            ...p,
            percentage: newPercentage,
            currentStep: stepsCompleted,
            steps: newSteps,
          };
        });

        return next;
      });
    }, tickInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [active, regionIds]);

  useEffect(() => {
    const allDone =
      progress.length > 0 &&
      progress.every((p) => p.currentStep >= ANALYSIS_STEPS.length);

    if (allDone && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsComplete(true);
    }
  }, [progress]);

  return { progress, isComplete };
}
